import React, { useEffect, useRef, useState } from 'react';

interface AdUnitProps {
  slotId: string;
  className?: string;
  format?: 'auto' | 'fluid' | 'rectangle' | 'horizontal' | 'vertical';
  client?: string;
  label?: string;
}

const AdUnit: React.FC<AdUnitProps> = ({
  slotId,
  className = '',
  format = 'auto',
  client = 'ca-pub-9133046126273004',
  label = 'Advertisement',
}) => {
  const adRef = useRef<HTMLModElement>(null);
  const [adStatus, setAdStatus] = useState<'loading' | 'filled' | 'unfilled'>('loading');
  const initialized = useRef(false);

  // Reset initialization state when slotId changes
  useEffect(() => {
    initialized.current = false;
    setAdStatus('loading');
  }, [slotId]);

  useEffect(() => {
    if (!adRef.current) return;
    // If we've already initialized this specific slot instance, skip
    if (initialized.current) return;

    const element = adRef.current;
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    // Function to attempt pushing the ad to the Google queue
    const pushAd = () => {
      if (initialized.current) return;

      // Critical check:
      // 1. offsetParent returns null if display:none
      // 2. offsetWidth > 0 ensures rendered size (width at least)
      // 3. isConnected ensures it's in the DOM
      // NOTE: We relaxed height check because initial height might be 0 or small
      if (
        element &&
        element.offsetParent !== null &&
        element.offsetWidth > 0 &&
        element.isConnected
      ) {
        try {
          // @ts-expect-error — adsbygoogle is injected by AdSense at runtime
          const ads = window.adsbygoogle || [];
          ads.push({});
          initialized.current = true;

          // Once initialized, we don't need to observe size anymore
          if (resizeObserver) resizeObserver.disconnect();
        } catch (e) {
          console.error('AdSense push error:', e);
        }
      }
    };

    // 1. Setup ResizeObserver to wait for container dimensions
    try {
      resizeObserver = new ResizeObserver((entries) => {
        // Use requestAnimationFrame to ensure layout is settled
        window.requestAnimationFrame(() => {
          if (!initialized.current) {
            for (const entry of entries) {
              if (entry.contentRect.width > 0 && element.offsetParent !== null) {
                pushAd();
              }
            }
          }
        });
      });
      resizeObserver.observe(element);
    } catch {
      // Fallback for environments without ResizeObserver
      if (element.offsetWidth > 0) pushAd();
    }

    // 2. Setup MutationObserver to watch for 'data-ad-status'
    mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-ad-status') {
          const status = element.getAttribute('data-ad-status');
          if (status === 'unfilled') {
            setAdStatus('unfilled');
            if (resizeObserver) resizeObserver.disconnect();
          } else if (status === 'filled') {
            setAdStatus('filled');
          }
        }
      }
    });
    mutationObserver.observe(element, { attributes: true });

    // Initial check
    const timer = setTimeout(() => {
      pushAd();
    }, 100);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
      clearTimeout(timer);
    };
  }, [slotId]);

  // Completely hide if unfilled
  if (adStatus === 'unfilled') return null;

  // Logic:
  // While loading, we MUST keep the element in DOM with some dimensions so AdSense can work.
  // However, we hide the "border" and "label" to avoid a blank frame.
  const isVisible = adStatus === 'filled';

  return (
    <div
      className={`ad-container w-full flex flex-col items-center justify-center my-6 transition-all duration-300 ${className}`}
    >
      {/* Only show decoration (border, background, label) when visible/filled */}
      <div
        className={`w-full flex flex-col items-center rounded-xl transition-all duration-300 ${
          isVisible
            ? 'p-4 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm border border-slate-100 dark:border-slate-800 hover:border-slate-200 dark:hover:border-slate-700'
            : 'border-0 bg-transparent'
        }`}
      >
        {isVisible && (
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3 opacity-60 select-none text-center">
            {label}
          </span>
        )}
        <div
          className={`w-full flex justify-center items-center rounded-lg overflow-hidden relative ${isVisible ? 'min-h-[100px] bg-slate-50 dark:bg-slate-800/50' : 'min-h-[50px]'}`}
        >
          <ins
            ref={adRef}
            className="adsbygoogle"
            style={{ display: 'block', width: '100%', textAlign: 'center' }}
            data-ad-client={client}
            data-ad-slot={slotId}
            data-ad-format={format}
            data-full-width-responsive="true"
          />
        </div>
      </div>
    </div>
  );
};

export default AdUnit;
